/**********************************************************************
 *
 * Copyright (C) 2026 Almendo Technologies GmbH
 * Copyright (C) 2026 Marcus Harringer
 *
 * All Rights Reserved.
 *
 * All information contained herein is, and remains the property of
 * Marcus Harringer. The intellectual and technical concepts contained
 * herein are proprietary to Marcus Harringer and may be covered
 * copyright law. Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written permission
 * is obtained from Marcus Harringer.
 *
 ***********************************************************************/
import getEnvironment from './utils/getEnvironment';
import { appsyncConfig, awsRegion, cognitoConfig, ssoConfig, storageConfig } from './config';
import { SSO_CALLBACK_LOGIN, SSO_CALLBACK_LOGOUT } from './utils/ssoLogin';

// Amplify config built only from src/env/backend.<env>.json (CDK backend) - aws-exports.js is not used.
const environment = getEnvironment();

const required = (name, value) => {
  if (!value) {
    throw new Error(`${name} is not set in src/env/backend.${environment}.json - required (REACT_APP_BLUSENSOR_ENV in .env.local selects the file)`);
  }
  return value;
};

const region = required('AWS_REGION', awsRegion);

// Hosted UI for the sign-in with an identity provider, only where the login domain is configured.
// The return pages belong to the app itself, so the origin follows the browser (app.<domain> or localhost).
function getOAuthConfig() {
  if (!ssoConfig.loginDomain) {
    return {};
  }
  return {
    oauth: {
      domain: ssoConfig.loginDomain,
      scopes: ['openid', 'email', 'profile', 'aws.cognito.signin.user.admin'],
      redirectSignIn: [`${window.location.origin}${SSO_CALLBACK_LOGIN}`],
      redirectSignOut: [`${window.location.origin}${SSO_CALLBACK_LOGOUT}`],
      responseType: 'code'
    }
  };
}

// Amplify v6 resources config. The identity pool hands out AWS credentials (user storage) to signed in users only.
const awsExportsApp = {
  Auth: {
    Cognito: {
      userPoolId: required('AWS_USER_POOLS_ID', cognitoConfig.userPoolId),
      userPoolClientId: required('AWS_USER_POOLS_CLIENT_ID', cognitoConfig.userPoolWebClientId),
      identityPoolId: required('AWS_COGNITO_IDENTITY_POOL_ID', cognitoConfig.identityPoolId),
      allowGuestAccess: false,
      loginWith: {
        email: true,
        ...getOAuthConfig()
      }
    }
  },
  API: {
    GraphQL: {
      endpoint: required('AWS_APPSYNC_GRAPHQL_ENDPOINT', appsyncConfig.graphqlEndpoint),
      region,
      defaultAuthMode: appsyncConfig.defaultAuthMode
    }
  },
  Storage: {
    S3: {
      bucket: required('AWS_USER_STORAGE_BUCKET', storageConfig.userFilesBucket),
      region
    }
  }
};

export default awsExportsApp;
